<script setup>
    import { computed, inject } from 'vue';
    import SubHead from '@/components/result/MLSRD-2026/SubHead.vue';
    import LevelRatio from '@/components/result/MLSRD-2026/LevelRatio.vue';
    import SubArea from '@/components/result/MLSRD-2026/SubArea.vue';
    import AnswerRates from '@/components/result/MLSRD-2026/AnswerRates.vue';

    // 부모에서 주입받은 데이터
    const classData = inject('classData');
    const loading = inject('loading');

    const pagetype = "literacy";
    const region = "seoul";

    // 학교급별 점수 구간 (표시용)
    const getScoreRanges = (schulType, gradeCode) => {
        const key = `${schulType}${gradeCode}`;
        const scoreRanges = {
            'E4': { '4': '1527~1700', '3': '1354~1526', '2': '1227~1353', '1': '1000~1226' },
            'E6': { '4': '1595~1800', '3': '1433~1594', '2': '1321~1432', '1': '1050~1320' },
            'M2': { '4': '1666~1900', '3': '1530~1665', '2': '1413~1529', '1': '1100~1412' },
            'H1': { '4': '1740~2000', '3': '1623~1739', '2': '1535~1622', '1': '1150~1534' }
        };
        return scoreRanges[key] || scoreRanges['E4'];
    };

    // 수준별 학생 분류 및 테이블 데이터 (korlevel 기반)
    const rateTableData = computed(() => {
        if (!classData.value || !classData.value.list) {
            const defaultScoreRanges = getScoreRanges('E', '4');
            return [
                { level: '4', scoreRange: defaultScoreRanges['4'], students: '' },
                { level: '3', scoreRange: defaultScoreRanges['3'], students: '' },
                { level: '2', scoreRange: defaultScoreRanges['2'], students: '' },
                { level: '1', scoreRange: defaultScoreRanges['1'], students: '' }
            ];
        }

        const data = classData.value;
        const scoreRanges = getScoreRanges(data.schul_type || 'E', data.grade_code || '4');
        
        // 각 수준별로 학생들을 분류 (korlevel 사용)
        const levelStudents = {
            '4': [],
            '3': [],
            '2': [],
            '1': []
        };

        data.list.forEach(student => {
            // korlevel이 없는 학생은 문해력 시험을 안친 것으로 간주하여 제외
            if (!student.korlevel) {
                return; // korlevel이 없으면 제외
            }
            
            const korLevel = String(student.korlevel);
            
            if (levelStudents[korLevel]) {
                levelStudents[korLevel].push(student.name || '');
            }
        });

        return ['4', '3', '2', '1'].map(level => ({
            level: level,
            scoreRange: scoreRanges[level],
            students: levelStudents[level].join(', ')
        }));
    });

    // 문해력 수준별 차트 데이터 (korlevel 기반)
    const literacyChartData = computed(() => {
        if (!classData.value || !classData.value.list) {
            return [
                { level: '1수준', percentage: 0, count: 0 },
                { level: '2수준', percentage: 0, count: 0 },
                { level: '3수준', percentage: 0, count: 0 },
                { level: '4수준', percentage: 0, count: 0 }
            ];
        }

        const data = classData.value;
        
        // korlevel별 학생 수 계산 (korlevel이 있는 학생만 포함)
        const levelCounts = { 1: 0, 2: 0, 3: 0, 4: 0 };
        let totalStudentsWithKorlevel = 0;
        
        data.list.forEach(student => {
            // korlevel이 없는 학생은 문해력 시험을 안친 것으로 간주하여 제외
            if (!student.korlevel) {
                return;
            }
            
            totalStudentsWithKorlevel++;
            const korLevel = parseInt(student.korlevel);
            if (levelCounts[korLevel] !== undefined) {
                levelCounts[korLevel]++;
            }
        });

        return [
            { 
                level: '1수준', 
                percentage: totalStudentsWithKorlevel > 0 ? Math.round((levelCounts[1] / totalStudentsWithKorlevel) * 100 * 10) / 10 : 0,
                count: levelCounts[1]
            },
            { 
                level: '2수준', 
                percentage: totalStudentsWithKorlevel > 0 ? Math.round((levelCounts[2] / totalStudentsWithKorlevel) * 100 * 10) / 10 : 0,
                count: levelCounts[2]
            },
            { 
                level: '3수준', 
                percentage: totalStudentsWithKorlevel > 0 ? Math.round((levelCounts[3] / totalStudentsWithKorlevel) * 100 * 10) / 10 : 0,
                count: levelCounts[3]
            },
            { 
                level: '4수준', 
                percentage: totalStudentsWithKorlevel > 0 ? Math.round((levelCounts[4] / totalStudentsWithKorlevel) * 100 * 10) / 10 : 0,
                count: levelCounts[4]
            }
        ];
    });

    // 우리 반 문해력 영역별 정답률 (korsub1~4rate)
    const ourClassPercentages = computed(() => {
        if (!classData.value) {
            return [75, 77, 73, 83]; // 기본값
        }
        
        const data = classData.value;
        return [
            parseInt(data.korsub1rate) || 0,  // 어휘
            parseInt(data.korsub2rate) || 0,  // 독해
            parseInt(data.korsub3rate) || 0,  // 비판및평가
            parseInt(data.korsub4rate) || 0   // 추론
        ];
    });

    // 전체 평균 문해력 영역별 정답률 (korsub1~4_total_rate)
    const overallPercentages = computed(() => {
        if (!classData.value) {
            return [52, 100, 77, 100]; // 기본값
        }
        
        const data = classData.value;
        return [
            parseInt(data.korsub1_total_rate) || 0,
            parseInt(data.korsub2_total_rate) || 0,
            parseInt(data.korsub3_total_rate) || 0,
            parseInt(data.korsub4_total_rate) || 0
        ];
    });
</script>

<template>
    <div class="slide-inner">
       <div class="content">
            <SubHead :type="pagetype" :region="region" title="문해력"/>            
            <div class="card-wrap">
                <LevelRatio 
                    :type="pagetype"                 
                    :table-data="rateTableData"
                    :chart-data="literacyChartData"
                />
                <SubArea :type="pagetype"/>
                <AnswerRates
                    :type="pagetype"                
                    :our-class-percentages="ourClassPercentages"
                    :overall-percentages="overallPercentages"               
                />
            </div>
       </div>
    </div>
</template>